import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';
import { KeyStringFilterInput, StringFilterInput, IntFilterInput } from 'app/api/common.api.types';
import { EMPTY, from, Observable } from 'rxjs';
import { concatAll, expand, map, toArray } from 'rxjs/operators';
import {
  UpdateRuleEntryInput,
  CreateRuleEntryInput,
  DeleteRuleEntryInput,
} from './API.service';

export interface RuleFilterInput {
  key: StringFilterInput;
  name: StringFilterInput;
  target: StringFilterInput;
  anaKey: StringFilterInput;
  sortOrder: IntFilterInput;
}

export interface RuleEntry {
  companyId: string; 
  key: string;
  axis: string;
  id: string;
  name: string;
  target: string;
  anaKey: string;
  sortOrder: number;
}

export interface RuleConnection {
  items: RuleEntry[];
  lastEvaluatedKey: any;
}

@Injectable({
  providedIn: 'root'
})
export class RuleAPIService {
  CreateRuleEntry(
    input: CreateRuleEntryInput
  ): Observable<RuleEntry> {
    const statement = `mutation CreateRuleEntry($input: CreateRuleEntryInput!) {
      createRuleEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        target
        anaKey
        sortOrder
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any) => {
        return response.data.createRuleEntry as RuleEntry;
      })
    );
  }

  UpdateRuleEntry(
    input: UpdateRuleEntryInput
  ): Observable<RuleEntry> {
    const statement = `mutation UpdateRuleEntry($input: UpdateRuleEntryInput!) {
      updateRuleEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        target
        anaKey
        sortOrder
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any) => {
        return response.data.updateRuleEntry as RuleEntry;
      })
    );
  }

  DeleteRuleEntry(
    input: DeleteRuleEntryInput
  ): Observable<RuleEntry> {
    const statement = `mutation DeleteRuleEntry($input: DeleteRuleEntryInput!) {
      deleteRuleEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        target
        anaKey
        sortOrder
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any) => {
        return response.data.deleteRuleEntry as RuleEntry;
      })
    );
  }

  Rule(
    companyId: string,
    key?: KeyStringFilterInput,
    filter?: RuleFilterInput,
    limit?: number,
  ): Observable<RuleEntry[]> {
    // tslint:disable-next-line: max-line-length
    const statement = `query Rule($id: String!, $key: KeyStringFilterInput, $filter: RuleFilterInput, $limit: Int, $exclusiveStartKey: AWSJSON) {
      company(id: $id) {
        rule(key: $key, filter: $filter, limit: $limit, exclusiveStartKey: $exclusiveStartKey) {
          items {
            companyId
            key
            axis
            id
            name
            target
            anaKey
            sortOrder
          }
          lastEvaluatedKey
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {};
    gqlAPIServiceArguments.id = companyId;
    if (key) {
      gqlAPIServiceArguments.key = key;
    }
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }

    return this.performQuery(statement, gqlAPIServiceArguments)
      .pipe(
        expand(conn => {
          const exclusiveStartKey = conn.lastEvaluatedKey;
          const args = { ...gqlAPIServiceArguments, exclusiveStartKey };
          return (conn.lastEvaluatedKey && JSON.parse(conn.lastEvaluatedKey) !== '')
            ? this.performQuery(statement, args)
            : EMPTY;
        }),
        map(conn => conn.items),
        concatAll(),
        toArray()
      );
  }

  private performQuery(statement: string, args: any = {}): Observable<RuleConnection> {
    return from(API.graphql(
      graphqlOperation(statement, args)
    )).pipe(
      map((response: any) => {
        return response.data.company.rule as RuleConnection;
      })
    );
  }

}
